/**
 * SOP (Statement of Purpose) Generator Page
 * - Only accessible after student has submitted at least one application
 * - AI-powered SOP generation with disclaimer
 */
import { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useStudentAuth } from '@/context/StudentAuthContext';
import { useAssetContext } from '@/context/AssetContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Badge } from '@/components/ui/badge';
import {
  FileText,
  ArrowLeft,
  Sparkles,
  AlertTriangle,
  Copy,
  Download,
  RefreshCw,
  Loader2,
  CheckCircle,
  Lock,
  GraduationCap,
  Building,
  User,
  Target
} from 'lucide-react';
import { toast } from 'sonner';

const API_URL = window.location.origin;

const SOPGeneratorPage = () => {
  const { user, token, loading: authLoading } = useStudentAuth();
  const { getLogo } = useAssetContext();
  const navigate = useNavigate();
  
  const [hasApplication, setHasApplication] = useState(false);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [profile, setProfile] = useState(null);
  const [applications, setApplications] = useState([]);
  
  // Form state
  const [selectedUniversity, setSelectedUniversity] = useState('');
  const [selectedProgram, setSelectedProgram] = useState('');
  const [careerGoals, setCareerGoals] = useState('');
  const [achievements, setAchievements] = useState('');
  const [whyThisProgram, setWhyThisProgram] = useState('');
  
  // Generated SOP
  const [generatedSOP, setGeneratedSOP] = useState('');
  const [sopHistory, setSopHistory] = useState([]);

  useEffect(() => {
    if (authLoading) return;
    if (!user) {
      navigate('/uac-desk/login');
      return;
    }
    loadData();
  }, [user, authLoading]);

  const loadData = async () => {
    setLoading(true);
    try {
      // Load profile
      const profileRes = await fetch(`${API_URL}/api/counselor/profile/${user.user_id}`, {
        credentials: 'include'
      });
      if (profileRes.ok) {
        const profileData = await profileRes.json();
        setProfile(profileData);
      }
      
      // Load applications using new endpoint
      const appsRes = await fetch(`${API_URL}/api/student/applications/${user.user_id}`, {
        credentials: 'include'
      });
      if (appsRes.ok) {
        const appsData = await appsRes.json();
        setApplications(appsData.applications || []);
        setHasApplication(appsData.has_application || false);
      } else {
        // Fallback - check for any saved search sessions as proxy
        const searchRes = await fetch(`${API_URL}/api/search/status/${user.user_id}`);
        if (searchRes.ok) {
          const searchData = await searchRes.json();
          // Allow access if they have any saved searches
          setHasApplication(searchData.searches_used > 0);
        }
      }
      
      // Load SOP history
      const sopRes = await fetch(`${API_URL}/api/student/sop-history/${user.user_id}`, {
        credentials: 'include'
      });
      if (sopRes.ok) {
        const sopData = await sopRes.json();
        setSopHistory(sopData.history || []);
      }
      
    } catch (error) {
      console.error('Error loading data:', error);
      // On error, allow access for now
      setHasApplication(true);
    }
    setLoading(false);
  };

  const generateSOP = async () => {
    if (!selectedUniversity || !selectedProgram) {
      toast.error('Please select a university and program');
      return;
    }
    
    setGenerating(true);
    
    try {
      const res = await fetch(`${API_URL}/api/student/generate-sop`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json'
        },
        credentials: 'include',
        body: JSON.stringify({
          user_id: user.user_id,
          university: selectedUniversity,
          program: selectedProgram,
          career_goals: careerGoals,
          achievements: achievements,
          why_this_program: whyThisProgram,
          profile: {
            name: user?.name,
            ...profile
          }
        })
      });
      
      if (res.ok) {
        const data = await res.json();
        setGeneratedSOP(data.sop);
        // Add to history
        setSopHistory(prev => [{
          sop_id: data.sop_id,
          university: data.university,
          program: data.program,
          content: data.sop,
          created_at: data.created_at
        }, ...prev]);
        toast.success('SOP generated successfully!');
      } else {
        const errorData = await res.json().catch(() => ({}));
        toast.error(errorData.detail || 'Failed to generate SOP. Please try again.');
        // Fallback: Generate locally with template
        const sop = generateLocalSOP();
        setGeneratedSOP(sop);
      }
    } catch (error) {
      console.error('SOP generation error:', error);
      // Fallback: Generate locally
      const sop = generateLocalSOP();
      setGeneratedSOP(sop);
      toast.info('Generated draft SOP locally');
    }
    
    setGenerating(false);
  };

  const generateLocalSOP = () => {
    const name = user?.name || 'Student';
    const qualification = profile?.highest_qualification || 'degree';
    const stream = profile?.stream || 'field';
    const percentage = profile?.percentage_gpa || 'excellent';
    
    return `STATEMENT OF PURPOSE

Dear Admissions Committee,

I am writing to express my strong interest in pursuing ${selectedProgram} at ${selectedUniversity}. As a ${qualification} graduate in ${stream} with ${percentage} academic performance, I am eager to advance my knowledge and skills in this dynamic field.

${careerGoals ? `\nMy career aspirations center on ${careerGoals}. I believe that the rigorous curriculum and research opportunities at ${selectedUniversity} will provide me with the foundation needed to achieve these goals.` : ''}

${achievements ? `\nThroughout my academic journey, I have demonstrated my commitment through ${achievements}. These experiences have not only honed my technical abilities but also developed my problem-solving and analytical skills.` : ''}

${whyThisProgram ? `\nI am particularly drawn to ${selectedUniversity}'s ${selectedProgram} program because ${whyThisProgram}. The university's reputation for excellence and its industry connections make it the ideal environment for my growth.` : ''}

I am confident that my academic background, coupled with my passion for learning and professional development, makes me a strong candidate for this program. I look forward to contributing to the diverse academic community at ${selectedUniversity} while working towards my professional goals.

Thank you for considering my application.

Sincerely,
${name}

---
⚠️ DISCLAIMER: This SOP is AI-generated and intended as a starting point only. Please personalize it with your unique experiences, achievements, and voice before submission. Universities value authentic, personal statements.`;
  };

  const copySOP = () => {
    navigator.clipboard.writeText(generatedSOP);
    toast.success('SOP copied to clipboard!');
  };

  const downloadSOP = () => {
    const blob = new Blob([generatedSOP], { type: 'text/plain' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `SOP_${selectedUniversity.replace(/\s+/g, '_')}_${Date.now()}.txt`;
    a.click();
    URL.revokeObjectURL(url);
    toast.success('SOP downloaded!');
  };

  if (authLoading || loading) {
    return (
      <div className="min-h-screen bg-slate-50 flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-ux-blue" />
      </div>
    );
  }

  // Locked state - no applications yet
  if (!hasApplication) {
    return (
      <div className="min-h-screen bg-slate-50">
        <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
          <div className="max-w-4xl mx-auto flex items-center gap-4">
            <Link to="/uac-desk/dashboard">
              <img 
                src={getLogo()} 
                alt="UniXperts" 
                className="h-7 w-auto object-contain hidden sm:block"
              />
            </Link>
            <div className="h-6 w-px bg-slate-200 hidden sm:block" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/uac-desk/dashboard')}
              className="text-slate-500 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 font-display">SOP Generator</h1>
              <p className="text-sm text-slate-500">AI-powered Statement of Purpose</p>
            </div>
          </div>
        </header>
        
        <main className="max-w-4xl mx-auto p-4 lg:p-8">
          <div className="bg-white rounded-2xl border border-slate-200 p-8 lg:p-12 text-center">
            <div className="w-20 h-20 rounded-2xl bg-slate-100 flex items-center justify-center mx-auto mb-6">
              <Lock className="w-10 h-10 text-slate-400" />
            </div>
            <h2 className="text-2xl font-bold text-slate-900 mb-3">SOP Generator Locked</h2>
            <p className="text-slate-600 max-w-md mx-auto mb-6">
              To access the AI-powered SOP generator, you need to first apply to at least one university through our Fast-Track Application process.
            </p>
            <Button 
              onClick={() => navigate('/uac-desk/fast-track')}
              className="bg-ux-blue hover:bg-ux-blue/90"
            >
              Start Fast-Track Application
            </Button>
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50">
      {/* Header */}
      <header className="bg-white border-b border-slate-200 px-4 lg:px-8 py-4">
        <div className="max-w-4xl mx-auto flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/uac-desk/dashboard">
              <img 
                src={getLogo()} 
                alt="UniXperts" 
                className="h-7 w-auto object-contain hidden sm:block"
              />
            </Link>
            <div className="h-6 w-px bg-slate-200 hidden sm:block" />
            <Button
              variant="ghost"
              size="sm"
              onClick={() => navigate('/uac-desk/dashboard')}
              className="text-slate-500 hover:text-slate-800"
            >
              <ArrowLeft className="w-4 h-4" />
            </Button>
            <div>
              <h1 className="text-xl font-bold text-slate-900 font-display">SOP Generator</h1>
              <p className="text-sm text-slate-500">AI-powered Statement of Purpose</p>
            </div>
          </div>
          <Badge className="bg-emerald-100 text-emerald-700">
            <CheckCircle className="w-3 h-3 mr-1" />
            Unlocked
          </Badge>
        </div>
      </header>

      <main className="max-w-4xl mx-auto p-4 lg:p-8">
        {/* Disclaimer Banner */}
        <div className="bg-amber-50 border border-amber-200 rounded-xl p-4 mb-6 flex items-start gap-3">
          <AlertTriangle className="w-5 h-5 text-amber-600 flex-shrink-0 mt-0.5" />
          <div>
            <p className="text-sm font-medium text-amber-800">Important Disclaimer</p>
            <p className="text-xs text-amber-700 mt-1">
              This AI-generated SOP is indicative and serves as a starting point. You should always personalize it with your unique experiences, voice, and specific details. Universities value authentic, personal statements.
            </p>
          </div>
        </div>

        <div className="grid lg:grid-cols-2 gap-6">
          {/* Input Form */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4 flex items-center gap-2">
              <FileText className="w-5 h-5 text-ux-blue" />
              SOP Details
            </h3>
            
            <div className="space-y-4">
              {/* University Selection */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <Building className="w-4 h-4 inline mr-1" />
                  University Name *
                </label>
                <Input
                  placeholder="e.g., Deakin University"
                  value={selectedUniversity}
                  onChange={(e) => setSelectedUniversity(e.target.value)}
                />
              </div>
              
              {/* Program */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <GraduationCap className="w-4 h-4 inline mr-1" />
                  Program/Course *
                </label>
                <Input
                  placeholder="e.g., Master of Data Science"
                  value={selectedProgram}
                  onChange={(e) => setSelectedProgram(e.target.value)}
                />
              </div>
              
              {/* Career Goals */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  <Target className="w-4 h-4 inline mr-1" />
                  Career Goals (Optional)
                </label>
                <Textarea
                  placeholder="What do you want to achieve after this program?"
                  value={careerGoals}
                  onChange={(e) => setCareerGoals(e.target.value)}
                  rows={2}
                />
              </div>
              
              {/* Achievements */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Key Achievements (Optional)
                </label>
                <Textarea
                  placeholder="Projects, internships, certifications, awards..."
                  value={achievements}
                  onChange={(e) => setAchievements(e.target.value)}
                  rows={2}
                />
              </div>
              
              {/* Why This Program */}
              <div>
                <label className="block text-sm font-medium text-slate-700 mb-1">
                  Why This Program? (Optional)
                </label>
                <Textarea
                  placeholder="What attracts you to this specific program?"
                  value={whyThisProgram}
                  onChange={(e) => setWhyThisProgram(e.target.value)}
                  rows={2}
                />
              </div>
              
              <Button 
                onClick={generateSOP}
                disabled={generating || !selectedUniversity || !selectedProgram}
                className="w-full bg-ux-blue hover:bg-ux-blue/90"
              >
                {generating ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Generating...
                  </>
                ) : (
                  <>
                    <Sparkles className="w-4 h-4 mr-2" />
                    Generate SOP
                  </>
                )}
              </Button>
            </div>
          </div>

          {/* Generated SOP Preview */}
          <div className="bg-white rounded-2xl border border-slate-200 p-6">
            <div className="flex items-center justify-between mb-4">
              <h3 className="font-semibold text-slate-900 flex items-center gap-2">
                <Sparkles className="w-5 h-5 text-violet-600" />
                Generated SOP
              </h3>
              {generatedSOP && (
                <div className="flex items-center gap-2">
                  <Button variant="outline" size="sm" onClick={copySOP}>
                    <Copy className="w-4 h-4" />
                  </Button>
                  <Button variant="outline" size="sm" onClick={downloadSOP}>
                    <Download className="w-4 h-4" />
                  </Button>
                </div>
              )}
            </div>
            
            {generatedSOP ? (
              <div className="bg-slate-50 rounded-xl p-4 max-h-[500px] overflow-y-auto">
                <pre className="whitespace-pre-wrap text-sm text-slate-700 font-sans leading-relaxed">
                  {generatedSOP}
                </pre>
              </div>
            ) : (
              <div className="bg-slate-50 rounded-xl p-8 text-center">
                <FileText className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                <p className="text-slate-500 text-sm">
                  Fill in the details and click "Generate SOP" to create your statement of purpose.
                </p>
              </div>
            )}
          </div>
        </div>

        {/* SOP History */}
        {sopHistory.length > 0 && (
          <div className="mt-6 bg-white rounded-2xl border border-slate-200 p-6">
            <h3 className="font-semibold text-slate-900 mb-4">Previously Generated SOPs</h3>
            <div className="space-y-2">
              {sopHistory.slice(0, 5).map((sop, idx) => (
                <div key={idx} className="flex items-center justify-between p-3 bg-slate-50 rounded-lg">
                  <div>
                    <p className="font-medium text-slate-800 text-sm">{sop.university}</p>
                    <p className="text-xs text-slate-500">{sop.program} • {new Date(sop.created_at).toLocaleDateString()}</p>
                  </div>
                  <Button variant="ghost" size="sm" onClick={() => setGeneratedSOP(sop.content)}>
                    <RefreshCw className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          </div>
        )}
      </main>
    </div>
  );
};

export default SOPGeneratorPage;
